import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, Filter, X } from "lucide-react";
import { toast } from "sonner";
import AddToPlanDialog from "@/components/AddToPlanDialog";

/**
 * Floating action bar shown while multi-select mode is active and at least one
 * recipe is selected. Wires two bulk operations:
 *   1. "Flytt til kategori" — change category for N recipes
 *   2. "Legg i ukeplan" — spread N recipes across consecutive days (via AddToPlanDialog)
 *
 * Props:
 *   - selectedIds (Set)
 *   - allRecipes (array)
 *   - onClearSelection() — close select mode + reset selection
 *   - onAfterChange() — reload recipes after a bulk operation completes
 */
export default function BulkActionsBar({ selectedIds, allRecipes, onClearSelection, onAfterChange }) {
  const { activeBoat } = useAuth();
  const [target, setTarget] = useState(null); // "category" | "meal-plan"
  const [category, setCategory] = useState("");
  const [moving, setMoving] = useState(false);

  const handleMoveCategory = async () => {
    if (!category) { toast.error("Velg en kategori"); return; }
    setMoving(true);
    let ok = 0, fail = 0;
    for (const id of selectedIds) {
      try {
        const r = allRecipes.find((x) => x.id === id);
        if (!r) { fail++; continue; }
        await api.updateRecipe(id, { ...r, category });
        ok++;
      } catch { fail++; }
    }
    setMoving(false);
    if (ok > 0) toast.success(`${ok} flyttet til ${category}`);
    if (fail > 0) toast.error(`${fail} feilet`);
    setTarget(null);
    setCategory("");
    onClearSelection?.();
    onAfterChange?.();
  };

  if (selectedIds.size === 0) return null;

  const selectedArray = Array.from(selectedIds);
  const firstRecipe = allRecipes.find((r) => r.id === selectedArray[0]) || null;

  return (
    <>
      {/* Floating action bar */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-stone-900 text-white rounded-2xl shadow-2xl px-3 py-2 flex items-center gap-2 max-w-[calc(100vw-2rem)]" data-testid="bulk-action-bar">
        <span className="text-xs font-semibold whitespace-nowrap pl-2 pr-1">{selectedIds.size} valgt</span>
        <span className="text-stone-600">·</span>
        <button
          onClick={() => setTarget("category")}
          className="inline-flex items-center gap-1 rounded-md bg-navy-700 hover:bg-navy-600 px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
          data-testid="bulk-move-category-btn"
        >
          <Filter className="h-3 w-3" /> Flytt til kategori
        </button>
        <button
          onClick={() => setTarget("meal-plan")}
          className="inline-flex items-center gap-1 rounded-md bg-emerald-700 hover:bg-emerald-600 px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
          data-testid="bulk-add-to-plan-btn"
        >
          <CalendarPlus className="h-3 w-3" /> Legg i ukeplan
        </button>
        <button
          onClick={onClearSelection}
          className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-stone-700 text-stone-300"
          data-testid="bulk-cancel-btn"
          title="Avbryt"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Move-to-category dialog */}
      <Dialog open={target === "category"} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="bulk-category-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Flytt {selectedIds.size} oppskrift{selectedIds.size === 1 ? "" : "er"} til
            </DialogTitle>
          </DialogHeader>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="bulk-category-select"><SelectValue placeholder="Velg kategori…" /></SelectTrigger>
            <SelectContent>
              {(() => {
                const customNames = (activeBoat?.recipe_categories || []).map((c) => c.name);
                const defaultNames = ["Middag","Frokost","Bakst","Kjøtt","Fisk","Pasta","Suppe","Dessert","Salat","Saus","Tradisjonsmat","Annet"];
                const all = [...new Set([...defaultNames, ...customNames])];
                return all.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>);
              })()}
            </SelectContent>
          </Select>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setTarget(null)} disabled={moving} className="flex-1 rounded-md border border-stone-200 px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50" data-testid="bulk-category-cancel">Avbryt</button>
            <button
              onClick={handleMoveCategory}
              disabled={moving || !category}
              className="flex-1 rounded-md bg-navy-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-900 disabled:opacity-50"
              data-testid="bulk-category-confirm"
            >
              {moving ? "Flytter…" : "Flytt"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meal-plan dialog — re-uses shared AddToPlanDialog with bulk array */}
      {target === "meal-plan" && (
        <AddToPlanDialog
          open={true}
          onOpenChange={(o) => { if (!o) setTarget(null); }}
          recipe={firstRecipe}
          bulkRecipeIds={selectedArray}
          allRecipes={allRecipes}
          onAfterSave={() => {
            setTarget(null);
            onClearSelection?.();
          }}
        />
      )}
    </>
  );
}
